import { API, graphqlOperation } from 'aws-amplify';
import { getConversationChatAssistant } from '../graphql/queries';
import { onChatAssistantResponse } from "../graphql/subscriptions";
import { buildApiUrl, getAuthHeaders, API_CONFIG, REASONING_AGENT_CONFIG } from './apiConfig';
import api from './api';
import { v4 as uuidv4 } from 'uuid';

class ChatCommunicationService {
  constructor(type = 'graphql') {
    this.type = type;
  }

  /**
   * Extracts text content from OpenAI's content format.
   * Handles both string format and array format (new OpenAI API).
   * Array format: [{type: 'reasoning', ...}, {type: 'text', text: '...'}]
   * @param {string|Array} content - The content to extract text from
   * @returns {string|null} - The extracted text or null if not found
   */
  extractTextFromContent(content) {
    if (typeof content === 'string' && content.trim().length > 0) {
      return content;
    }
    
    if (Array.isArray(content)) {
      // Handle OpenAI's new content array format
      // Look for items with type 'text' and extract their text property
      const textItems = content.filter(item => item?.type === 'text' && item?.text);
      if (textItems.length > 0) {
        return textItems.map(item => item.text).join('\n\n');
      }
      
      // Fallback: try to find any item with text-like content
      const fallbackItem = content.find(item => 
        item?.text || item?.content || item?.message
      );
      if (fallbackItem) {
        return fallbackItem.text || fallbackItem.content || fallbackItem.message;
      }
    }
    
    return null;
  }

  async sendMessage(params, options = {}) {
    if (this.type === 'http') {
      // Nuevo método asíncrono
      return this.sendAsynchronousMessage(params, options);
      // Código anterior comentado - descomentar si hay problemas
      // return this.sendLangGraphMessage(params, options);
    } else {
      return this.sendGraphQLMessage(params, options);
    }
  }

  async sendLangGraphMessage(params, options = {}) {
    const {
      message_text,
      tenant_id,
      sender_uuid,
      assistant_uuid,
      conversation_uuid,
      channel,
      llmConfig, // Optional LLM config override for model selection
    } = params;

    const payload = {
      "assistant_id": assistant_uuid,
      "input": {
        "messages": [
          {
            "type": "human",
            "content": [
              {
                "type": "text",
                "text": message_text,
              }
            ],
          },
        ],
        // Include params with LLM config only if provided
        ...(llmConfig && {
          "params": {
            "assistant": {
              "llm_config": llmConfig
            },
            "rag": {
              "llm_config": llmConfig
            },
            "response_synthesis": {
              "llm_config": llmConfig
            }
          }
        }),
        assistant_id: assistant_uuid,
        message_text: message_text || 'No message',
        tenant_id: tenant_id || 'default-tenant',
        conversation_uuid: conversation_uuid,
        sender_uuid: sender_uuid || 'default-sender',
        assistant_uuid: assistant_uuid || 'default-assistant',
        channel: channel || ''
      }
    }

    try {
      let thread_id = conversation_uuid;
      if (!thread_id) {
        const createThreadUrl = `${API_CONFIG.langGraphURL}/threads`;
        const createThreadResponse = await fetch(createThreadUrl, {
          method: 'POST',
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout(API_CONFIG.timeouts.request)
        });
  
        
        if (!createThreadResponse.ok) {
          const errorData = await createThreadResponse.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${createThreadResponse.status}`);
        }
        
        const threadData = await createThreadResponse.json();
        thread_id = threadData["thread_id"]
      }

      const url = `${API_CONFIG.langGraphURL}/threads/${thread_id}/runs/wait`;
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const messagesNumber = data["messages"] ? data["messages"].length : 0;
      const last_message = messagesNumber > 0 ? data["messages"][messagesNumber - 1] : { content: '' };

      const dataResponse = {
        "msgCode": "message_sent_successfully",
        "requestId": uuidv4(),
        "senderUuid": sender_uuid,
        "messageText": last_message["content"],
        "htmlContent": null,
        "conversationUuid": thread_id,
        "messageUuid": uuidv4(),
        "assistantUuid": assistant_uuid,
        "createdAt": new Date().toISOString().replace('Z', '')
      }


      return {
        success: true,
        data: dataResponse,
        type: 'http'
      };
    } catch (error) {
      console.error('HTTP request failed:', error);
      return {
        success: false,
        error: error.message,
        type: 'http'
      };
    }
  }

  async sendAsynchronousMessage(inputParams, options = {}) {
    const {
      message_text,
      assistant_uuid,
      conversation_uuid,
      channel,
      file_uuids,
      user_uuid, // Get user_uuid from params
      params: customParams, // Custom params (e.g., party_uuid for ucp_graph agent)
    } = inputParams;

    const payload = {
      message_text: message_text,
      assistant_uuid: assistant_uuid,
      conversation_uuid: conversation_uuid,
      channel: channel || 'WEB'
    };

    // Add custom params (like party_uuid) if provided
    if (customParams && Object.keys(customParams).length > 0) {
      payload.params = customParams;
    }

    // Add file_uuids to payload if provided
    if (file_uuids && Array.isArray(file_uuids) && file_uuids.length > 0) {
      payload.file_uuids = file_uuids;
    }

    try {
      // Use api.post() instead of fetch to leverage interceptors for token management
      // Backend will extract user_uuid from the token automatically
      const url = '/v1/message/send-asynchronous';
      const response = await api.post(url, payload);

      return {
        success: true,
        data: response.data, // axios already parses JSON in response.data
        type: 'http'
      };
    } catch (error) {
      console.error('Asynchronous message request failed:', error);
      
      // Handle axios errors (error.response contains server response)
      const errorMessage = error.response?.data?.message 
        || error.message 
        || `HTTP error! status: ${error.response?.status || 'unknown'}`;
      
      return {
        success: false,
        error: errorMessage,
        type: 'http'
      };
    }
  }

  /**
   * Extract DAG progress information from streaming event data
   * The actual response structure is:
   * - output.planner.preplan - Planning phase with goals
   * - output.director.dag.graph - DAG nodes with id, description, type, instruction
   * - output.manager.execution_path - Current execution progress
   * - output.{NODE_ID} - Node execution status (empty = running, has text_output = complete)
   * - output.reasoning.result - Final output
   */
  extractDAGProgress(eventType, eventData) {
    const results = [];

    // If no data, return null
    if (!eventData || typeof eventData !== 'object') {
      return null;
    }

    // The actual progress data is in the "output" field
    const output = eventData.output || {};
    
    // Build a map of node descriptions from the DAG graph
    const nodeDescriptions = {};
    const nodeTypes = {};
    const nodeInstructions = {};
    
    if (output.director && output.director.dag && output.director.dag.graph) {
      output.director.dag.graph.forEach(node => {
        nodeDescriptions[node.id] = node.description;
        nodeTypes[node.id] = node.type;
        nodeInstructions[node.id] = node.instruction;
      });
    }

    // Check for planner phase in output
    if (output.planner && output.planner.preplan) {
      const preplan = output.planner.preplan;
      const goals = preplan.goals || [];
      const reasoningLevel = output.planner.reasoning_level || 'L1';
      
      results.push({
        nodeName: 'Planner',
        description: preplan.descriptive_plan 
          ? preplan.descriptive_plan
          : `Goals: ${goals.slice(0, 2).join(', ')}`,
        status: 'completed',
        timestamp: new Date().toISOString(),
        eventType: 'planner',
        details: {
          goals,
          topics: preplan.topics,
          hintNodes: preplan.hint_nodes,
          reasoningLevel,
          acceptanceCriteria: preplan.acceptance_criteria,
        },
      });
    }

    // Check for director phase (DAG definition) in output
    if (output.director && output.director.dag && output.director.dag.graph) {
      const graph = output.director.dag.graph;
      
      results.push({
        nodeName: 'Director',
        description: `Created execution plan with ${graph.length} step${graph.length !== 1 ? 's' : ''}`,
        status: 'completed',
        timestamp: new Date().toISOString(),
        eventType: 'director',
        details: {
          nodeCount: graph.length,
          nodes: graph.map(n => ({
            id: n.id,
            type: n.type,
            description: n.description,
            instruction: n.instruction,
            dependsOn: n.depends_on,
          })),
        },
      });

      // Emit each DAG node with its actual description from the graph
      graph.forEach((node, index) => {
        results.push({
          nodeName: node.id,
          description: node.description, // Use the actual description from the DAG
          instruction: node.instruction,
          status: 'pending',
          timestamp: new Date().toISOString(),
          eventType: 'dag_node',
          nodeType: node.type,
          stepNumber: index + 1,
          totalSteps: graph.length,
          details: {
            type: node.type,
            instruction: node.instruction,
            dependsOn: node.depends_on,
            notes: node.notes,
          },
        });
      });
    }

    // Check for manager phase (execution path) - this tells us which nodes are being executed
    if (output.manager && output.manager.execution_path) {
      const executionPath = output.manager.execution_path;
      
      if (executionPath.length > 0) {
        // The last node in execution_path is the one currently being executed
        const currentNodeId = executionPath[executionPath.length - 1];
        const currentNodeDescription = nodeDescriptions[currentNodeId] || currentNodeId;
        
        results.push({
          nodeName: 'Manager',
          description: `Step ${executionPath.length}: ${currentNodeDescription}`,
          status: 'completed',
          timestamp: new Date().toISOString(),
          eventType: 'manager',
          details: {
            executionPath,
            currentNode: currentNodeId,
          },
        });
        
        // Update status for nodes that are in the execution path
        executionPath.forEach((nodeId, index) => {
          const isLast = index === executionPath.length - 1;
          results.push({
            nodeName: nodeId,
            description: nodeDescriptions[nodeId] || `Executing ${nodeId}`,
            instruction: nodeInstructions[nodeId],
            status: isLast ? 'running' : 'completed', // Last one is running, others completed
            timestamp: new Date().toISOString(),
            eventType: 'node_execution',
            nodeType: nodeTypes[nodeId],
            stepNumber: index + 1,
            details: {
              type: nodeTypes[nodeId],
              instruction: nodeInstructions[nodeId],
            },
          });
        });
      }
    }

    // Check for individual node outputs (when node completes or errors)
    const knownKeys = ['planner', 'director', 'manager', 'reasoning'];
    const nodeKeys = Object.keys(output).filter(key => !knownKeys.includes(key));
    
    nodeKeys.forEach((nodeId) => {
      const nodeOutput = output[nodeId];
      if (nodeOutput && typeof nodeOutput === 'object') {
        const hasTextOutput = nodeOutput.text_output !== undefined && nodeOutput.text_output !== '';
        const hasError = nodeOutput.error !== undefined && nodeOutput.error !== '';
        const isEmpty = Object.keys(nodeOutput).length === 0;
        
        // Get the description from our DAG map
        const description = nodeDescriptions[nodeId] || `Processing ${nodeId}`;
        
        let status = 'running';
        let statusDescription = description;
        
        if (hasError) {
          status = 'error';
          statusDescription = `Failed: ${description}`;
        } else if (hasTextOutput) {
          status = 'completed';
          statusDescription = `Completed: ${description}`;
        } else if (isEmpty) {
          status = 'running';
          statusDescription = description; // Just show the description while running
        }
        
        results.push({
          nodeName: nodeId,
          description: statusDescription,
          status,
          timestamp: new Date().toISOString(),
          eventType: 'node_execution',
          nodeType: nodeTypes[nodeId],
          details: {
            type: nodeTypes[nodeId],
            instruction: nodeInstructions[nodeId],
            output: nodeOutput.text_output,
            error: nodeOutput.error,
          },
        });
      }
    });

    // Check for final reasoning result in output
    if (output.reasoning) {
      const reasoning = output.reasoning;
      const hasResult = reasoning.result && reasoning.result.trim().length > 0;
      
      results.push({
        nodeName: 'Final Result',
        description: hasResult 
          ? 'Reasoning complete - generating response' 
          : 'Preparing final response...',
        status: hasResult ? 'completed' : 'running',
        timestamp: new Date().toISOString(),
        eventType: 'reasoning',
        details: {
          result: reasoning.result,
        },
      });
    }

    // Return all extracted progress items, or null if none
    return results.length > 0 ? results : null;
  }

  /**
   * Deep merge two objects, combining nested properties
   * Used to accumulate streaming response state
   */
  deepMerge(target, source) {
    const output = { ...target };
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key];
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
          // Only overwrite with non-empty values
          output[key] = source[key];
        } else if (!(key in target)) {
          // Add key if it doesn't exist in target
          output[key] = source[key];
        }
      });
    }
    
    return output;
  }

  /**
   * Check if value is a plain object
   */
  isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * Send a message to the Reasoning Agent (AI Copilot)
   * Uses the streaming endpoint /runs/stream for real-time DAG progress
   */
  async sendReasoningAgentMessage(params, options = {}) {
    const {
      message_text,
      conversation_uuid,
      sender_uuid,
      onProgress, // Callback for DAG progress updates
    } = params;

    // Use the exact payload format provided by the backend engineer
    const payload = {
      "assistant_id": "reasoning",
      "stream_subgraphs": true,
      "input": {
        "params": {
          "reasoning": {
            "reasoning_input": message_text
          }
        }
      }
    };

    try {
      // Use /runs/stream endpoint for streaming DAG progress (architect's recommended approach)
      const url = `${REASONING_AGENT_CONFIG.baseURL}/runs/stream`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': REASONING_AGENT_CONFIG.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Handle streaming response for DAG progress updates
      let finalContent = '';
      let allChunks = [];
      let accumulatedState = {}; // Accumulate the entire response state
      let allParsedData = []; // Store all parsed data objects
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { value, done } = await reader.read();
        
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        
        // Store all chunks for final processing
        allChunks.push(chunk);

        // Parse the chunk to extract DAG progress information
        // The chunk format is typically: event: <event_type>\ndata: <json_data>\n\n
        const lines = chunk.split('\n');
        
        let currentEvent = null;
        let currentData = null;

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.replace('event:', '').trim();
          } else if (line.startsWith('data:')) {
            const dataStr = line.replace('data:', '').trim();
            if (dataStr) {
              try {
                currentData = JSON.parse(dataStr);
              } catch (e) {
                currentData = dataStr;
              }
            }
          } else if (line === '' && currentEvent && currentData) {
            // End of an event block, process the event
            // Store all parsed data for later processing
            if (typeof currentData === 'object') {
              allParsedData.push({ event: currentEvent, data: currentData });
              // Merge into accumulated state (deep merge for nested objects)
              accumulatedState = this.deepMerge(accumulatedState, currentData);
            }
            
            // Call onProgress callback with DAG node information
            if (onProgress) {
              const progressItems = this.extractDAGProgress(currentEvent, currentData);
              if (progressItems) {
                // extractDAGProgress now returns an array of progress items
                if (Array.isArray(progressItems)) {
                  progressItems.forEach(item => onProgress(item));
                } else {
                  onProgress(progressItems);
                }
              }
            }

            // ONLY use output.reasoning.result as the FINAL answer
            // This is the definitive result from the reasoning agent
            if (currentData && currentData.output && currentData.output.reasoning) {
              const reasoning = currentData.output.reasoning;
              const extractedContent = this.extractTextFromContent(reasoning.result);
              if (extractedContent) {
                finalContent = extractedContent;
              }
            }
            
            // Legacy fallback: check old location (reasoning.result at top level)
            if (!finalContent && currentData && currentData.reasoning && currentData.reasoning.result) {
              const result = currentData.reasoning.result;
              const extractedContent = this.extractTextFromContent(result);
              if (extractedContent) {
                finalContent = extractedContent;
              }
            }

            // Reset for next event
            currentEvent = null;
            currentData = null;
          }
        }
      }

      // After streaming is complete, extract the final content
      // PRIMARY SOURCE: Always use output.reasoning.result as the definitive answer
      // This is the final result from the reasoning agent
      if (accumulatedState.output && accumulatedState.output.reasoning && accumulatedState.output.reasoning.result) {
        const result = accumulatedState.output.reasoning.result;
        const extractedContent = this.extractTextFromContent(result);
        if (extractedContent) {
          finalContent = extractedContent;
        }
      }
      
      // Only use fallbacks if output.reasoning.result is not available
      if (!finalContent) {
        
        // FALLBACK 1: Check top-level reasoning.result (legacy structure)
        if (!finalContent && accumulatedState.reasoning && accumulatedState.reasoning.result) {
          const result = accumulatedState.reasoning.result;
          const extractedContent = this.extractTextFromContent(result);
          if (extractedContent) {
            finalContent = extractedContent;
          }
        }
        
        // FALLBACK 2: Check messages for AI response content
        if (!finalContent && accumulatedState.messages && accumulatedState.messages.length > 0) {
          for (const msg of accumulatedState.messages) {
            if (msg.type === 'ai' && msg.content && msg.content.trim().length > 0) {
              finalContent = msg.content;
              break;
            }
          }
        }
        
        // FALLBACK 3: Check the LAST node in execution path for text_output
        // This is the final step output before reasoning aggregates it
        if (!finalContent && accumulatedState.output) {
          const executionPath = accumulatedState.output.manager?.execution_path || [];
          
          if (executionPath.length > 0) {
            // Get the last node's output
            const lastNodeId = executionPath[executionPath.length - 1];
            const lastNodeOutput = accumulatedState.output[lastNodeId];
            
            if (lastNodeOutput && lastNodeOutput.text_output && lastNodeOutput.text_output.trim().length > 0) {
              finalContent = lastNodeOutput.text_output;
            }
          }
        }
      }

      // Build final response data
      const data = {
        messages: finalContent ? [{ content: finalContent }] : [],
        content: finalContent,
        chunks: allChunks,
      };

      // Check if data is null or has errors
      if (!data) {
        throw new Error('No response data received from reasoning agent');
      }
      
      if (data.__error__) {
        const error = data.__error__;
        console.error('❌ LangGraph API Error:', error);
        throw new Error(`${error.error}: ${error.message}`);
      }

      // Try different possible response structures
      let messageContent = '';
      
      if (data && data.messages && data.messages.length > 0) {
        // Structure 1: messages array with content
        const lastMessage = data.messages[data.messages.length - 1];
        messageContent = lastMessage.content || lastMessage.text || '';
      } else if (data.content) {
        // Structure 2: direct content property
        messageContent = data.content;
      } else if (data.response) {
        // Structure 3: response property
        messageContent = data.response;
      } else if (data.output) {
        // Structure 4: output property
        messageContent = data.output;
      } else if (typeof data === 'string') {
        // Structure 5: direct string response
        messageContent = data;
      }

      // If still no content, check for any text-like properties
      if (!messageContent) {
        // Look for any property that might contain the response
        for (const key of Object.keys(data)) {
          const value = data[key];
          if (typeof value === 'string' && value.trim().length > 0) {
            messageContent = value;
            break;
          }
        }
      }

      // Final fallback: check accumulated state for any useful content
      // BUT EXCLUDE input fields that contain what we sent TO the agent
      if (!messageContent && Object.keys(accumulatedState).length > 0) {
        // Try to find any text content in the accumulated state
        // EXCLUDE paths that contain input data
        const excludedPaths = [
          'params',  // Contains input parameters
          'reasoning_input',  // The input we sent
          'planner_input',  // Input to planner
          'messages',  // May contain input messages
        ];
        
        const findTextContent = (obj, path = '') => {
          for (const key of Object.keys(obj)) {
            const value = obj[key];
            const currentPath = path ? `${path}.${key}` : key;
            
            // Skip excluded paths
            if (excludedPaths.some(excluded => currentPath.includes(excluded))) {
              continue;
            }
            
            if (typeof value === 'string' && value.trim().length > 50) {
              // Found a substantial text content
              return value;
            } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              const found = findTextContent(value, currentPath);
              if (found) return found;
            }
          }
          return null;
        };
        
        const foundContent = findTextContent(accumulatedState);
        if (foundContent) {
          messageContent = foundContent;
        }
      }

      // PRIMARY: Always prioritize output.reasoning.result
      // Extract reasoningResult directly from accumulatedState.output.reasoning.result
      // Check multiple possible paths to ensure we get it
      let reasoningResult = null;
      
      // Path 1: output.reasoning.result (primary)
      if (accumulatedState.output?.reasoning?.result) {
        const result = accumulatedState.output.reasoning.result;
        const extractedText = this.extractTextFromContent(result);
        if (extractedText) {
          reasoningResult = extractedText;
        }
      }
      
      // Path 2: Check if it's nested differently
      if (!reasoningResult && accumulatedState.output) {
        // Check all keys in output for reasoning
        const outputKeys = Object.keys(accumulatedState.output);
        
        for (const key of outputKeys) {
          if (key === 'reasoning' && accumulatedState.output[key]?.result) {
            const result = accumulatedState.output[key].result;
            const extractedText = this.extractTextFromContent(result);
            if (extractedText) {
              reasoningResult = extractedText;
              break;
            }
          }
        }
      }
      
      // Path 3: Check top-level reasoning.result (legacy)
      if (!reasoningResult && accumulatedState.reasoning?.result) {
        const result = accumulatedState.reasoning.result;
        const extractedText = this.extractTextFromContent(result);
        if (extractedText) {
          reasoningResult = extractedText;
        }
      }
      
      // Use reasoningResult as the primary source for finalMessageText
      // Only use finalContent (from streaming) or messageContent (fallbacks) if reasoningResult is not available
      const finalMessageText = reasoningResult || finalContent || messageContent || '';
      
      return {
        success: true,
        data: {
          messageText: finalMessageText,
          reasoningResult: reasoningResult, // Always from output.reasoning.result
          conversationUuid: uuidv4(), // Generate a new UUID for this streaming session
          agentType: 'reasoning', // Important: identifies this as a reasoning agent response
          assistantName: REASONING_AGENT_CONFIG.agentName,
          accumulatedState: accumulatedState, // Include accumulated state for debugging
        },
        type: 'reasoning'
      };
    } catch (error) {
      console.error('❌ Reasoning agent request failed:', error);
      return {
        success: false,
        error: error.message,
        type: 'reasoning'
      };
    }
  }

  async sendHttpMessage(params, options = {}) {
    // Usar exactamente los parámetros que vienen en params (snake_case)
    const {
      message_text,
      tenant_id,
      sender_uuid,
      assistant_uuid,
      conversation_uuid,
      channel,
      metadata,
      attachment
    } = params;

    const requestBody = {
      message_text: message_text || 'No message',
      tenant_id: tenant_id || 'default-tenant',
      conversation_uuid: conversation_uuid, // Usar exactamente el valor enviado
      sender_uuid: sender_uuid || 'default-sender',
      assistant_uuid: assistant_uuid || 'default-assistant',
      channel: channel || ''
    };

    // Agregar metadata si existe
    if (metadata && Object.keys(metadata).length > 0) {
      requestBody.metadata = metadata;
    }

    // Agregar attachment si existe
    if (attachment) {
      requestBody.attachment = attachment;
    }

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.endpoints.sendMessage), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(API_CONFIG.timeouts.request)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        type: 'http'
      };
    } catch (error) {
      console.error('HTTP request failed:', error);
      return {
        success: false,
        error: error.message,
        type: 'http'
      };
    }
  }

  async sendGraphQLMessage(params, options = {}) {
    // GraphQL removed - commented out
    // try {
    //   const result = await API.graphql({
    //     query: getConversationChatAssistant,
    //     variables: { input: params },
    //     authMode: 'AMAZON_COGNITO_USER_POOLS'
    //   });

    //   if (result.errors?.length) {
    //     return {
    //       success: false,
    //       error: result.errors[0].message,
    //       type: 'graphql'
    //     };
    //   }

    //   return {
    //     success: true,
    //     data: result.data.getConversationChatAssistant,
    //     type: 'graphql'
    //   };
    // } catch (error) {
    //   console.error('GraphQL request failed:', error);
    //   return {
    //     success: false,
    //     error: error.message,
    //     type: 'graphql'
    //   };
    // }
    return {
      success: false,
      error: 'GraphQL is deprecated. Use HTTP endpoints instead.',
      type: 'graphql'
    };
  }

  subscribeToResponse(responseId, options = {}) {
    if (this.type === 'http') {
      return this.createHttpSubscription(responseId, options);
    } else {
      return this.createGraphQLSubscription(responseId, options);
    }
  }

  createHttpSubscription(responseId, options = {}) {
    // Para HTTP, simulamos un subscription con polling
    const { onMessage, onError, onComplete } = options;
    let retryCount = 0;

    const pollStatus = async () => {
      try {
        const response = await fetch(buildApiUrl(`${API_CONFIG.endpoints.messageStatus}/${responseId}`), {
          headers: getAuthHeaders(),
          signal: AbortSignal.timeout(API_CONFIG.timeouts.response)
        });

        if (response.ok) {
          const data = await response.json();
          onMessage && onMessage(data);

          if (data.status === 'completed' || data.status === 'error' || data.status === 'failed') {
            clearInterval(interval);
            onComplete && onComplete(data);
          }

          // Reset retry count on successful response
          retryCount = 0;
        } else {
          throw new Error(`Status check failed: ${response.status}`);
        }
      } catch (error) {
        retryCount++;

        if (retryCount >= API_CONFIG.retry.maxRetries) {
          clearInterval(interval);
          onError && onError(new Error(`Max retries exceeded: ${error.message}`));
        }
      }
    };

    // Polling inicial
    pollStatus();

    const interval = setInterval(pollStatus, API_CONFIG.retry.retryDelay);

    // Retornamos un objeto que simula la API de subscription
    return {
      unsubscribe: () => {
        clearInterval(interval);
      }
    };
  }

  createGraphQLSubscription(responseId, options = {}) {
    // GraphQL removed - commented out
    // return API.graphql({
    //   ...graphqlOperation(onChatAssistantResponse, { id: responseId }),
    //   authMode: 'AMAZON_COGNITO_USER_POOLS'
    // }).subscribe(options);
    return {
      unsubscribe: () => {}
    };
  }

  // Método para verificar el estado de la conexión HTTP
  async checkHttpConnection() {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.endpoints.health), {
        method: 'GET',
        headers: getAuthHeaders(),
        signal: AbortSignal.timeout(5000)
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

export { ChatCommunicationService };
